import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { SlidersHorizontal, X } from "lucide-react";
import { api } from "../lib/api";
import { ProductCard } from "../components/ProductCard";
import { PromoBanner } from "../components/PromoBanner";

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [meta, setMeta] = useState({ categories: [], brands: [], stores: [] });
  const [loading, setLoading] = useState(true);
  const [showBrands, setShowBrands] = useState(false);
  const category = params.get("category") || "";
  const subcategory = params.get("subcategory") || "";
  const store = params.get("store") || "";
  const search = params.get("q") || "";
  const [searchInput, setSearchInput] = useState(search);
  const [sugg, setSugg] = useState(null);
  const [showSugg, setShowSugg] = useState(false);
  const [trending, setTrending] = useState([]);

  useEffect(() => { api.get("/searches/trending").then((r) => setTrending(r.data.terms || [])).catch(() => {}); }, []);

  useEffect(() => {
    if (searchInput.trim().length < 2) { setSugg(null); return; }
    const t = setTimeout(() => {
      api.get("/products/suggest", { params: { q: searchInput.trim() } }).then((r) => setSugg(r.data)).catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    api.get("/categories").then((r) => setMeta(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const q = {};
    if (category) q.category = category;
    if (subcategory) q.subcategory = subcategory;
    if (store) q.store = store;
    if (search) q.q = search;
    api.get("/products", { params: q }).then((r) => setProducts(r.data)).finally(() => setLoading(false));
  }, [category, subcategory, store, search]);

  const setFilter = (updates) => {
    const next = new URLSearchParams(params);
    Object.entries(updates).forEach(([k, v]) => { if (v) next.set(k, v); else next.delete(k); });
    setParams(next);
  };

  const activeCat = meta.categories.find((c) => c.name === category);

  const chip = (active) =>
    `px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap border ${active ? "bg-[#306D29] text-[#FBF5DD] border-[#306D29]" : "bg-white border-[#0D530E]/12 text-[#0D530E] hover:border-[#306D29]"}`;

  return (
    <>
      <PromoBanner placement="shop_top" />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
      <div className="flex items-center gap-3 mb-1">
        <span className="text-2xl">🇬🇧</span>
        <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#306D29]">United Kingdom Store</p>
      </div>
      <h1 className="font-display text-5xl sm:text-6xl font-light tracking-tight text-[#0D530E] mb-6">{category || (search ? `“${search}”` : "Shop UK")}</h1>

      {/* SEARCH */}
      <div className="relative mb-6">
        <form onSubmit={(e) => { e.preventDefault(); setShowSugg(false); setFilter({ q: searchInput }); }} className="flex gap-2">
          <input data-testid="shop-search-input" value={searchInput}
            onChange={(e) => { setSearchInput(e.target.value); setShowSugg(true); }}
            onFocus={() => setShowSugg(true)} onBlur={() => setTimeout(() => setShowSugg(false), 150)}
            placeholder="Search products, brands or needs — e.g. “dry skin”, “baby”, “protein”"
            className="flex-1 bg-white border border-[#0D530E]/12 rounded-full px-5 py-3 text-sm focus:ring-2 focus:ring-[#306D29] focus:outline-none" />
          {search && <button type="button" data-testid="shop-search-clear" onClick={() => { setSearchInput(""); setFilter({ q: "" }); }} className="px-4 rounded-full border border-[#0D530E]/12 text-sm text-[#0D530E]">Clear</button>}
          <button type="submit" data-testid="shop-search-btn" className="bg-[#306D29] hover:bg-[#0D530E] text-white font-semibold px-6 rounded-full text-sm" style={{ transition: "background-color 0.2s ease" }}>Search</button>
        </form>
        {showSugg && sugg && (sugg.tags.length + sugg.brands.length + sugg.products.length > 0) && (
          <div className="absolute z-30 left-0 right-0 mt-2 bg-white border border-[#0D530E]/12 rounded-2xl shadow-[0_18px_50px_rgba(13,83,14,0.15)] p-3 max-w-2xl" data-testid="search-suggestions">
            {sugg.tags.length > 0 && (
              <div className="mb-2">
                <p className="text-[10px] uppercase tracking-wider font-bold text-[#5C5C5C] px-1 mb-1">Popular tags</p>
                <div className="flex flex-wrap gap-1.5">
                  {sugg.tags.map((t) => (
                    <button key={t} onMouseDown={() => { setSearchInput(t); setShowSugg(false); setFilter({ q: t }); }} data-testid={`sugg-tag-${t}`}
                      className="text-xs font-medium text-[#0D530E] bg-[#E7E1B1]/60 hover:bg-[#E7E1B1] rounded-full px-3 py-1 capitalize">#{t}</button>
                  ))}
                </div>
              </div>
            )}
            {sugg.brands.length > 0 && (
              <div className="mb-2">
                <p className="text-[10px] uppercase tracking-wider font-bold text-[#5C5C5C] px-1 mb-1">Brands</p>
                <div className="flex flex-wrap gap-1.5">
                  {sugg.brands.map((b) => (
                    <button key={b} onMouseDown={() => { setSearchInput(b); setShowSugg(false); setFilter({ q: b }); }} data-testid={`sugg-brand-${b}`}
                      className="text-xs font-medium text-[#0D530E] border border-[#0D530E]/12 hover:border-[#306D29] rounded-full px-3 py-1">{b}</button>
                  ))}
                </div>
              </div>
            )}
            {sugg.products.length > 0 && (
              <div>
                <p className="text-[10px] uppercase tracking-wider font-bold text-[#5C5C5C] px-1 mb-1">Products</p>
                {sugg.products.map((p) => (
                  <button key={p.id} onMouseDown={() => { setShowSugg(false); navigate(`/product/${p.id}`); }} data-testid={`sugg-product-${p.id}`}
                    className="block w-full text-left text-sm text-[#0D530E] hover:bg-[#E7E1B1]/40 rounded-lg px-2 py-1.5 truncate">{p.name}</button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {!search && trending.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap mb-5 -mt-1" data-testid="trending-searches">
          <span className="text-xs text-[#5C5C5C]">Trending:</span>
          {trending.map((t) => (
            <button key={t} onClick={() => { setSearchInput(t); setFilter({ q: t }); }} data-testid={`trending-${t}`}
              className="text-xs font-medium text-[#306D29] bg-white border border-[#0D530E]/12 hover:border-[#306D29] rounded-full px-3 py-1 capitalize" style={{ transition: "border-color 0.2s ease" }}>{t}</button>
          ))}
        </div>
      )}

      {/* CATEGORY CHIPS */}
      <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1 -mx-1 px-1">
        <button data-testid="filter-cat-all" onClick={() => setFilter({ category: "", subcategory: "" })} className={chip(!category)}>All products</button>
        {meta.categories.map((c) => (
          <button key={c.name} data-testid={`filter-cat-${c.name}`} onClick={() => setFilter({ category: c.name, subcategory: "" })}
            className={chip(category === c.name)} style={{ transition: "background-color 0.2s ease, border-color 0.2s ease" }}>{c.name}</button>
        ))}
      </div>

      {/* SUBCATEGORY CHIPS */}
      {activeCat && activeCat.subcategories.length > 0 && (
        <div className="flex gap-2 flex-wrap mt-3">
          <button onClick={() => setFilter({ subcategory: "" })}
            className={`px-3 py-1.5 rounded-full text-xs font-medium ${!subcategory ? "bg-[#0D530E] text-[#FBF5DD]" : "bg-[#E7E1B1]/60 text-[#0D530E] hover:bg-[#E7E1B1]"}`}
            style={{ transition: "background-color 0.2s ease" }}>All {activeCat.name}</button>
          {activeCat.subcategories.map((s) => (
            <button key={s} data-testid={`filter-sub-${s}`} onClick={() => setFilter({ subcategory: s })}
              className={`px-3 py-1.5 rounded-full text-xs font-medium ${subcategory === s ? "bg-[#0D530E] text-[#FBF5DD]" : "bg-[#E7E1B1]/60 text-[#0D530E] hover:bg-[#E7E1B1]"}`}
              style={{ transition: "background-color 0.2s ease" }}>{s}</button>
          ))}
        </div>
      )}

      {/* TOOLBAR */}
      <div className="flex items-center justify-between mt-6 mb-6 border-t border-[#0D530E]/10 pt-4">
        <p className="text-sm text-[#306D29]/80">{loading ? "Loading…" : `${products.length} product${products.length === 1 ? "" : "s"}`}{store && ` · ${store}`}</p>
        <button onClick={() => setShowBrands((v) => !v)} data-testid="toggle-brands"
          className="flex items-center gap-2 text-sm font-medium text-[#0D530E] border border-[#0D530E]/12 rounded-full px-4 py-1.5 hover:border-[#306D29]" style={{ transition: "border-color 0.2s ease" }}>
          {store ? <X size={15} onClick={(e) => { e.stopPropagation(); setFilter({ store: "" }); }} /> : <SlidersHorizontal size={15} />}
          {store ? store : "Store"}
        </button>
      </div>

      {showBrands && (
        <div className="flex gap-2 flex-wrap mb-8 -mt-2">
          {(meta.stores || []).map((b) => (
            <button key={b} data-testid={`filter-store-${b}`} onClick={() => { setFilter({ store: store === b ? "" : b }); setShowBrands(false); }}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border ${store === b ? "bg-[#306D29] text-[#FBF5DD] border-[#306D29]" : "bg-white border-[#0D530E]/12 text-[#0D530E] hover:border-[#306D29]"}`}>{b}</button>
          ))}
        </div>
      )}

      {/* GRID */}
      {loading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 sm:gap-5">
          {[...Array(10)].map((_, i) => <div key={i} className="aspect-square rounded-xl bg-[#E7E1B1]/50 animate-pulse" />)}
        </div>
      ) : products.length === 0 ? (
        <p className="text-[#306D29]/70 py-12" data-testid="shop-empty">No products found for this filter.</p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 sm:gap-5" data-testid="product-grid">
          {products.map((p) => <ProductCard key={p.id} product={p} />)}
        </div>
      )}
    </div>
    </>
  );
}
